import numeral from "numeral";
import React, { Fragment, useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Modal } from "src/hooks/modal";
import { MenuComponent } from "../../../components/MenuComponent";
import { changeStatusShipment } from "./actions/ActionChangeStatusShipment";
import {
  Badge,
  BadgeProgressValue,
  BadgeStatusValue,
  Button,
  IndexTable,
  LegacyCard,
  Page,
  Pagination as PaginationPR,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { PolarisEmptyState } from "src/components/shopify/PolarisEmptyState";
import { ShipmentFilter } from "./components/filter";
import {
  ShipmentLogStatus,
  ShipmentProduct,
  SortOrder,
  useSupplierShipmentListQuery,
} from "src/graphql/codegen_graphql";
import { PackageMajor } from "@shopify/polaris-icons";
import { Toast } from "src/hooks/toast";
import { ContextUser } from "src/context/ContextUser";
import { PrinteSku4x3 } from "./print/PrinteSku4x3";

function RenderMenu({
  id,
  status,
  onClickA4,
  onClickShip,
  onClickReceive,
  onClickRequestPickup,
  ship,
}: {
  id: any;
  status: any;
  onClickA4?: any;
  onClickShip: any;
  onClickReceive: any;
  onClickRequestPickup: any;
  ship: ShipmentProduct;
}) {
  const { merchant } = useContext(ContextUser);
  const { t } = useTranslation();
  let refPrint4x3: HTMLButtonElement;

  const menu = [];

  if (!["PENDING", "DELETED"].includes(status)) {
    menu.push({
      label: "Print",
      actived: true,
      onClick: () => refPrint4x3.click(),
    });
  }

  if (!ship.requestDate) {
    menu.push({
      label: "Request Pickup",
      actived: true,
      onClick: onClickRequestPickup,
    });
  }

  if (status === "PENDING") {
    menu.push({
      label: t("ship"),
      actived: true,
      onClick: onClickShip,
    });

    menu.push({
      label: t("delete"),
      actived: true,
      onClick: onClickReceive,
    });
  }

  if (menu.length === 0) {
    return <></>;
  }

  const barcode = ship
    ? [
        {
          code: ship.shipmentCode,
          price: "",
          supplier: {
            name: merchant.name,
          },
          sku: [
            {
              sku_id: ship.shipmentCode,
              color: "",
              size: "",
              expiredDate: undefined,
            },
          ],
        },
      ]
    : [];

  return (
    <>
      <div style={{ display: "none" }}>
        {/* <PrinterQR codes={`SHP${numeral(Number(id)).format("000000")}`} refButton={refButton} title="L192 Shipment" /> */}
        <PrinteSku4x3 sku={barcode} refPrint={(ref: any) => (refPrint4x3 = ref)} />
      </div>
      <MenuComponent
        items={menu}
        menuButton={
          <Link to="#" style={{ textDecoration: "none" }}>
            <i className="os-icon os-icon-more-vertical" style={{ fontSize: "15pt" }}></i>
          </Link>
        }
      />
    </>
  );
}

export function InventoryShipmentLogScreen(props: any) {
  const { t } = useTranslation();
  const history = useHistory();
  const query = new URLSearchParams(history.location.search);
  const [order, setOrder] = useState<SortOrder | string | null>(
    query.get("order") ? query.get("order") : SortOrder.Desc,
  );
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  const { data, loading, refetch } = useSupplierShipmentListQuery({
    fetchPolicy: "no-cache",
    variables: {
      limit: Number(limit),
      offset: Number(offset) * Number(limit),
      status: selectedStatus as ShipmentLogStatus[],
      orderBy: order as SortOrder,
      shipmentId: search,
    },
  });

  const totalPerPage = data ? data.supplierShipmentList?.data?.length || 0 : 0;

  return (
    <Page
      title={t("shipment_log")}
      fullWidth
      primaryAction={{
        content: t("create_shipment"),
        url: "/applications/inventories/list",
      }}
    >
      <LegacyCard>
        <ShipmentFilter
          orderBy={order as SortOrder}
          setOrderBy={setOrder}
          status={selectedStatus as ShipmentLogStatus[]}
          setStatus={setSelectedStatus}
          search={search}
          setSearch={setSearch}
        />
        <LegacyCard.Section flush>
          <PaginationPR
            type="table"
            label={`${offset * limit + 1}-${totalPerPage < limit ? totalPerPage : limit * (offset + 1)} of ${
              data ? data.supplierShipmentList?.page?.total : 0
            } ships`}
            hasNext={(data ? totalPerPage || 0 : 0) <= limit}
            hasPrevious={offset > 0}
            onNext={() => setOffset(offset + 1)}
            onPrevious={() => setOffset(offset - 1)}
          />
        </LegacyCard.Section>
        <LegacyCard.Section flush>
          <IndexTable
            headings={[
              { title: "#", alignment: "center" },
              { title: t("shipment"), alignment: "center" },
              { title: t("date"), alignment: "center" },
              { title: t("request_date"), alignment: "center" },
              { title: t("departed_date"), alignment: "center" },
              { title: t("received_date"), alignment: "center" },
              { title: t("units_sent"), alignment: "center" },
              { title: t("units_received"), alignment: "center" },
              { title: t("package"), alignment: "center" },
              { title: t("status"), alignment: "center" },
              { title: "", alignment: "center" },
            ]}
            itemCount={data ? data.supplierShipmentList?.data?.length || 0 : 0}
            loading={loading}
            selectable={false}
            emptyState={<PolarisEmptyState />}
          >
            {data &&
              data.supplierShipmentList?.data?.map((item, index) => {
                let statusShipment = "";
                let statusColor: BadgeStatusValue = BadgeStatusValue.New;
                let progress: BadgeProgressValue = BadgeProgressValue.Incomplete;

                switch (item?.status) {
                  case "PENDING":
                    statusShipment = t("pending");
                    statusColor = BadgeStatusValue.AttentionStrong;
                    break;
                  case "SHIPPED":
                    statusShipment = t("shipped");
                    statusColor = BadgeStatusValue.WarningStrong;
                    progress = BadgeProgressValue.PartiallyComplete;
                    break;
                  case "RECEIVED":
                    statusShipment = t("received");
                    statusColor = BadgeStatusValue.InfoStrong;
                    progress = BadgeProgressValue.PartiallyComplete;
                    break;
                  case "ARRIVED":
                    statusShipment = t("arrived_at_l192");
                    statusColor = BadgeStatusValue.SuccessStrong;
                    progress = BadgeProgressValue.Complete;
                    break;
                  case "ARRIVED_AT_SHIPPER":
                    statusShipment = t("arrived_at_shipper");
                    statusColor = BadgeStatusValue.SuccessStrong;
                    progress = BadgeProgressValue.Complete;
                    break;
                  case "DEPARTED":
                    statusShipment = `${t("departed_from_country")},${item.departedDate} [ ${
                      item.tracking_number![item.tracking_number!.length - 1]
                    } ]`;
                    statusColor = BadgeStatusValue.CriticalStrong;
                    progress = BadgeProgressValue.Complete;
                    break;
                  default:
                    statusShipment = t(item?.status + "") + "";
                    break;
                }

                const createAt = new Date(item?.createdAt + "").getTime();
                const departedDate = new Date(item?.departedDate + "").getTime();
                const receivedDate = new Date(item?.receivedDate + "").getTime();
                const requestDate = new Date(item?.requestDate + "").getTime();

                const lastDate = [createAt, departedDate, receivedDate, requestDate]
                  .filter((x) => !!x)
                  .reduce((a: any, b: any) => (b < a ? a : b), 0);

                return (
                  <React.Fragment key={index}>
                    <IndexTable.Row id={item?.id + ""} position={index}>
                      <IndexTable.Cell>
                        <div className="text-center">{index + 1}</div>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <div className="text-center">
                          <Link to={"/applications/inventories/detail/" + item?.id}>
                            <small>
                              <strong>SHP{numeral(Number(item?.id)).format("000000")}</strong>
                            </small>
                          </Link>
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <div className="text-center">
                          {lastDate === createAt ? (
                            <small>
                              <strong className="text-primary">{item?.createdAt}</strong>
                            </small>
                          ) : (
                            <small>{item?.createdAt}</small>
                          )}
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell className={!item?.requestDate ? "bg-light" : ""}>
                        <div className="text-center">
                          {requestDate === lastDate ? (
                            <small>
                              <strong className="text-primary">{item?.requestDate ? item?.requestDate : "--"}</strong>
                            </small>
                          ) : (
                            <small>{item?.requestDate ? item?.requestDate : "--"}</small>
                          )}
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell className={!item?.departedDate ? "bg-light" : ""}>
                        <div className="text-center">
                          {lastDate === departedDate ? (
                            <small>
                              <strong className="text-primary">{item?.departedDate ? item.departedDate : "--"}</strong>
                            </small>
                          ) : (
                            <small>{item?.departedDate ? item.departedDate : "--"}</small>
                          )}
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell className={!item?.receivedDate ? "bg-light" : ""}>
                        <div className="text-center">
                          {lastDate === receivedDate ? (
                            <small>
                              <strong className="text-primary">{item?.receivedDate ? item.receivedDate : "--"}</strong>
                            </small>
                          ) : (
                            <small>{item?.receivedDate ? item.receivedDate : "--"}</small>
                          )}
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <div className="text-center">{item?.qty}</div>
                      </IndexTable.Cell>
                      <IndexTable.Cell
                        className={item?.qty !== item?.received ? "bg-danger text-light" : "bg-success text-light"}
                      >
                        <div className="text-center">{item?.received}</div>
                      </IndexTable.Cell>
                      <IndexTable.Cell className={!item?.package ? "bg-light" : ""}>
                        <div className="text-center">
                          {item?.package ? (
                            <Button
                              variant="plain"
                              tone={!!item?.package?.uuid ? "success" : "critical"}
                              target={item.package.uuid ? "_blank" : undefined}
                              url={item?.package?.uuid ? `https://p.l192.com/p/${item.package.uuid}` : "#"}
                              size="slim"
                              icon={item.package.uuid ? (PackageMajor as any) : undefined}
                              removeUnderline
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  (item?.package?.uuid ? item?.package?.uuid : item.package?.id) + "",
                                );
                                Toast.onShow(
                                  `Copied package: ${item?.package?.uuid ? item?.package?.uuid : item.package?.id}`,
                                  "info",
                                  "right",
                                );
                              }}
                            >
                              {
                                (
                                  <small>
                                    <strong>{item?.package?.id + ""}</strong>
                                  </small>
                                ) as any
                              }
                            </Button>
                          ) : (
                            <small>--</small>
                          )}
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <div className="text-center">
                          <span>
                            {statusShipment.split(",").map((n, i) => (
                              <Fragment key={i}>
                                {i > 0 && <br />}
                                {/* <small className={(i === 0 && statusColor) + ""}>{n}</small> */}
                                <Badge
                                  tone={i === 0 ? statusColor : "new"}
                                  progress={i === 0 ? progress : "incomplete"}
                                >
                                  {t(n.toLowerCase())}
                                </Badge>
                              </Fragment>
                            ))}
                          </span>
                        </div>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <div className="text-center">
                          <RenderMenu
                            id={item?.id}
                            // onClickA4={() => setA4(item?.id || 0)}
                            // onClickA4={() => handlePrintItem(Number(item?.id || 0))}
                            ship={item as any}
                            onClickRequestPickup={() => {
                              changeStatusShipment(Number(item?.id), "REQUEST").then((res) => {
                                if (res) refetch();
                              });
                            }}
                            onClickShip={() => {
                              Modal.dialog({
                                title: t("ship_confirm"),
                                message: t("ship_confirm_message"),
                                buttons: [
                                  { title: t("no"), class: "danger" },
                                  {
                                    title: t("yes"),
                                    onPress: async () => {
                                      const res = await changeStatusShipment(Number(item?.id), "SHIPPED");
                                      if (res) refetch();
                                    },
                                  },
                                ],
                              });
                            }}
                            onClickReceive={() => {
                              Modal.dialog({
                                message: t("delete_confirm_message"),
                                title: t("delete_confirm"),
                                buttons: [
                                  { title: "No", class: "danger" },
                                  {
                                    title: "Yes",
                                    onPress: async () => {
                                      const res = await changeStatusShipment(Number(item?.id), "DELETED");
                                      if (res) refetch();
                                    },
                                  },
                                ],
                              });
                            }}
                            status={item?.status}
                          />
                        </div>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  </React.Fragment>
                );
              })}
          </IndexTable>
        </LegacyCard.Section>
      </LegacyCard>
    </Page>
  );
}
